import logger from "../../../utils/logger";
import React, { useState, useRef, useEffect } from "react";
import {
  Card,
  Col,
  Container,
  Label,
  Row,
  Button,
  Form,
  Spinner,
  InputGroup,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  CardBody,
} from "reactstrap";

// redux
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { sendVerifyCode } from "../../../slices/apps/authSlice";

// router
import { Link, useNavigate, useSearchParams } from "react-router-dom";

// Formik validation
import * as Yup from "yup";
import { useFormik } from "formik";

// react input mask
import { IMaskInput } from "react-imask";

// görsel importlar
import Logo from "../../../assets/images/logo/metiner_avantaj_yatay_logo.png";
import Party from "../../../assets/images/party_mix.png";
import BoschLogo from "../../../assets/images/bosch-service-logo.png";

const Login = () => {
  document.title = "Müşteri Girişi | Metiner Avantaj+";

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const companyId = searchParams.get("companyId");

  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [isCustomerScrolledToBottom, setIsCustomerScrolledToBottom] =
    useState(false);

  const customerModalBodyRef = useRef(null);

  const { sendVerifyCodeLoading } = useSelector(
    (state) => ({
      sendVerifyCodeLoading: state.auth.sendVerifyCodeLoading,
    }),
    shallowEqual
  );

  const toggleCustomerModal = () => {
    setCustomerModalOpen(!customerModalOpen);
  };

  const checkCustomerScrollOnModalOpened = () => {
    if (customerModalBodyRef.current) {
      const { scrollHeight, clientHeight } = customerModalBodyRef.current;
      if (scrollHeight <= clientHeight) {
        setIsCustomerScrolledToBottom(true);
      }
    }
  };

  const resetCustomerScrollStateOnModalClosed = () => {
    setIsCustomerScrolledToBottom(false);
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      phoneNumber: "",
      customerKvkkConsent: false,
    },
    validationSchema: Yup.object({
      phoneNumber: Yup.string()
        .required("Lütfen telefon numarası giriniz.")
        .length(10, "Telefon numarası 10 haneli olmalıdır."),
      customerKvkkConsent: Yup.boolean().oneOf(
        [true],
        "Müşteri Aydınlatma Metni'ni onaylamanız gerekmektedir."
      ),
    }),
    onSubmit: async (values) => {
      try {
        const resultAction = await dispatch(
          sendVerifyCode({
            phoneNumber: values.phoneNumber,
            userType: 1,
            companyId: companyId,
          })
        ).unwrap();

        if (
          resultAction.result === "existing_customer_no_visit" ||
          resultAction.result === "already_registered"
        ) {
          navigate("/customer/error", {
            state: { userStatus: resultAction.result },
          });
        } else {
          navigate("/customer/verify-otp", {
            state: { phoneNumber: values.phoneNumber, companyId: companyId },
          });
        }
      } catch (error) {
        logger.error("Giriş hatası:", error);
      }
    },
  });

  useEffect(() => {
    if (!companyId) {
      navigate("/404");
    }
  }, [companyId, navigate]);

  const handleCustomerScroll = (e) => {
    const bottom =
      e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 1;
    if (bottom) {
      setIsCustomerScrolledToBottom(true);
    }
  };

  const handleCustomerKvkkApproval = () => {
    validation.setFieldValue("customerKvkkConsent", true);
    toggleCustomerModal();
  };

  const handleCustomerCheckboxChange = () => {
    if (!validation.values.customerKvkkConsent) {
      toggleCustomerModal();
    } else {
      validation.setFieldValue("customerKvkkConsent", false);
    }
  };

  return (
    <React.Fragment>
      <div className="auth-page-wrapper d-flex justify-content-center align-items-center min-vh-100">
        <div className="auth-page-content">
          <Container>
            <Row className="justify-content-center">
              <Col lg={12}>
                <br />
              </Col>
              <Col md={8} lg={6} xl={5}>
                <Card className="overflow-hidden">
                  <CardBody className="p-lg-5 p-4">
                    <div className="text-center">
                      <Link to="/" className="d-inline-block auth-logo">
                        <img src={Logo} alt="Metiner Avantaj+" height="46" />
                      </Link>
                    </div>
                    <div className="text-center mt-4 mb-4">
                      <div className="d-inline-block auth-logo mb-3">
                        <img
                          src={Party}
                          alt="Metiner Avantaj+ Party"
                          height="110"
                        />
                      </div>
                      <div>
                        <h3 className="text-primary mb-2 fw-bold text-uppercase">
                          Tebrikler!!!
                        </h3>
                        <h5 className="text-primary fw-bold pt-2">
                          Servis ve lastik işlemlerinde geçerli 1.000TL hediye
                          çeki kazandınız.{" "}
                        </h5>
                        <h6 className="text-primary pt-4 fs-12">
                          *Hediyenizi aktifleştirmek için telefonunuzu
                          doğrulayınız.
                        </h6>
                        <h6 className="text-primary fs-12 mb-0">
                          *Kayıt işlemi 1 dakikanızı alacaktır.{" "}
                        </h6>
                      </div>
                    </div>
                    <Form onSubmit={validation.handleSubmit} autoComplete="off">
                      <Row className="gy-3">
                        <Col lg={12}>
                          <Label>Telefon Numarası</Label>
                          <InputGroup>
                            <span
                              className="input-group-text"
                              style={{
                                width: "44px",
                                justifyContent: "center",
                              }}
                            >
                              0
                            </span>
                            <IMaskInput
                              inputMode="numeric"
                              mask="000 000 00 00"
                              unmask={true}
                              name="phoneNumber"
                              placeholder="5XX XXX XX XX"
                              value={validation.values.phoneNumber}
                              className={`form-control ${
                                validation.touched.phoneNumber &&
                                validation.errors.phoneNumber
                                  ? "is-invalid"
                                  : ""
                              }`}
                              style={{ zIndex: 0 }}
                              onAccept={(value) =>
                                validation.setFieldValue("phoneNumber", value)
                              }
                            />
                          </InputGroup>
                          {validation.touched.phoneNumber &&
                            validation.errors.phoneNumber && (
                              <div className="invalid-feedback d-block">
                                {validation.errors.phoneNumber}
                              </div>
                            )}
                        </Col>

                        <Col lg={12}>
                          <div className="d-flex align-items-start">
                            <Input
                              type="checkbox"
                              id="customerKvkkConsent"
                              name="customerKvkkConsent"
                              className="me-2"
                              checked={validation.values.customerKvkkConsent}
                              onChange={handleCustomerCheckboxChange}
                              style={{ cursor: "pointer" }}
                              invalid={
                                !!(
                                  validation.touched.customerKvkkConsent &&
                                  validation.errors.customerKvkkConsent
                                )
                              }
                            />
                            <Label
                              htmlFor="customerKvkkConsent"
                              className="form-check-label mb-2"
                            >
                              <span
                                style={{
                                  cursor: "pointer",
                                  color: "#007bff",
                                  textDecoration: "underline",
                                }}
                              >
                                Müşteri Aydınlatma Metni
                              </span>
                              'ni okudum, anladım ve onaylıyorum.
                            </Label>
                          </div>
                          {validation.touched.customerKvkkConsent &&
                            validation.errors.customerKvkkConsent && (
                              <div className="invalid-feedback d-block">
                                {validation.errors.customerKvkkConsent}
                              </div>
                            )}
                        </Col>

                        <Col lg={12} className="mt-4">
                          <Button
                            type="submit"
                            color="primary"
                            className="w-100"
                            disabled={
                              !validation.values.customerKvkkConsent ||
                              sendVerifyCodeLoading
                            }
                          >
                            {sendVerifyCodeLoading ? (
                              <>
                                <Spinner size="sm" className="me-2" />{" "}
                                Yükleniyor...
                              </>
                            ) : (
                              "Hediyeni Aktifleştir"
                            )}
                          </Button>
                        </Col>
                      </Row>
                    </Form>
                    <div className="mt-4 d-flex align-items-center justify-content-center">
                      <div className="d-inline-block auth-logo">
                        <img
                          src={BoschLogo}
                          alt="Bosch Car Service"
                          height="68"
                        />
                      </div>
                      <ul className="fs-11 mb-0" style={{ width: "240px" }}>
                        <li>
                          Kampanya Metiner Bosch Car Service şubelerinde
                          geçerlidir.
                        </li>
                        <li>5.000TL ve üzeri işlemlerde kullanılır.</li>
                      </ul>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>

        <footer className="footer h-auto pb-2">
          <Container>
            <Row>
              <Col lg={12}>
                <div className="text-center text-muted">
                  <p className="mb-2" style={{ letterSpacing: "0.8px" }}>
                    Metiner Avantaj+ &copy; {new Date().getFullYear()} | 0 352 3
                    555 444 | metiner@boschservice.com.tr
                    <br />
                    <i className="mdi mdi-heart text-danger"></i> by{" "}
                    <a
                      href="https://kodexis.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: "none", color: "#007bff" }}
                    >
                      Kodexis
                    </a>
                  </p>
                </div>
              </Col>
            </Row>
          </Container>
        </footer>
      </div>

      <Modal
        isOpen={customerModalOpen}
        toggle={toggleCustomerModal}
        centered
        size="lg"
        onOpened={checkCustomerScrollOnModalOpened}
        onClosed={resetCustomerScrollStateOnModalClosed}
      >
        <style>
          {`
            .modal-body-custom-scrollbar::-webkit-scrollbar {
              display: none;
            }
          `}
        </style>

        <ModalHeader toggle={toggleCustomerModal}>
          Müşteri Aydınlatma Metni
        </ModalHeader>

        <ModalBody
          ref={customerModalBodyRef}
          onScroll={handleCustomerScroll}
          className="modal-body-custom-scrollbar"
          style={{
            maxHeight: "60vh",
            overflowY: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          <h6>Veri Sorumlusu</h6>
          <p>
            Metiner Avantaj+ Akaryakıt Madeni Yağ San. ve Tic. Ltd. Şti.
            ("Şirket")
          </p>

          <h6>Kapsam</h6>
          <p>
            Bu aydınlatma metni, Şirketimiz ile iş birliği yapan kuaför
            işletmelerinde yürütülen kampanyalar kapsamında, indirim veya hediye
            kuponu kazanan müşterilerin kişisel verilerinin işlenmesine
            ilişkindir.
          </p>

          <h6>İşlenen Kişisel Veriler</h6>
          <ul>
            <li>Ad, soyad</li>
            <li>Telefon numarası</li>
            <li>Plaka bilgisi</li>
            <li>Kampanya kodu ve kullanım bilgisi</li>
            <li>İşlem yapılan işyeri bilgisi</li>
            <li>Kupon tutarı ve kullanım zamanı</li>
          </ul>

          <h6>Veri İşleme Amaçları</h6>
          <ul>
            <li>
              Kampanya kuponlarının doğrulanması ve kullanımlarının takibi
            </li>
            <li>Kampanya sürecinde müşteriye bilgilendirme yapılması</li>
            <li>
              Kampanya sonrası memnuniyet ve istatistiksel analizlerin
              yürütülmesi
            </li>
            <li>Olası uyuşmazlık durumlarında hakların korunması</li>
          </ul>

          <h6>Verilerin Aktarıldığı Taraflar</h6>
          <p>
            Kişisel verileriniz; kampanyanın yürütülmesi amacıyla yalnızca iş
            birliği yapılan işletmeyle, kampanya sisteminin teknik altyapısını
            sağlayan tedarikçilerle ve yasal zorunluluk halinde ilgili kamu
            kurumlarıyla paylaşılabilir.
          </p>

          <h6>Veri Toplama Yöntemi ve Hukuki Sebep</h6>
          <p>
            Kişisel verileriniz; kampanya katılım formu, SMS doğrulama veya
            kupon kodu kullanımı sırasında açık rızanızla elektronik ortamda
            toplanmaktadır. İşleme faaliyetleri, KVKK madde 5/1 (açık rıza) ve
            5/2-(ç) (sözleşmenin kurulması veya ifasıyla doğrudan ilgili olma)
            hükümlerine dayanır.
          </p>

          <h6>Kişisel Veri Sahibinin Hakları</h6>
          <p>
            KVKK madde 11 uyarınca; kişisel verilerinize ilişkin bilgi talep
            etme, düzeltilmesini, silinmesini veya işlenmesinin kısıtlanmasını
            isteme gibi haklara sahipsiniz. Bu haklarınızı
            muhasebe@metiner.com.tr adresine e-posta göndererek
            kullanabilirsiniz.
          </p>
        </ModalBody>

        <ModalFooter className="justify-content-center mt-3">
          <Button
            color="primary"
            onClick={handleCustomerKvkkApproval}
            // disabled={!isCustomerScrolledToBottom}
          >
            Okudum, Onaylıyorum
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default Login;
